// entityStore.ts — drill-through navigation stack for the Alkio Mobile v4 shell.
//
// The universal `Sheet` (Pattern 1 in the implementation guide) is a recursive
// graph descent: tapping an entity opens ITS sheet, whose linkPills open THEIR
// sheets, and so on. We model that as a stack of entity refs held outside React
// so the global `window.__openEntity` bridge and the React tree stay in sync.

export interface EntityRef {
  /** Path-form entity type used by the PWA data API (e.g. "steps", "milestones"). */
  type: string;
  id: string;
}

type Listener = () => void;

let suppressNextPopstate = 0;

let stack: EntityRef[] = [];
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

export function getStack(): EntityRef[] {
  return stack;
}

export function buildEntityUrl(type: string, id: string): string {
  return `#v4/entity/${encodeURIComponent(type)}/${encodeURIComponent(id)}`;
}

/** Push a new entity onto the drill-through stack (opens a sheet on top). */
export function pushEntity(type: string, id: string): void {
  if (!type || !id) return;
  const newStack = [...stack, { type, id }];
  stack = newStack;
  if (typeof history !== 'undefined') {
    history.pushState({ entityStack: newStack }, '', buildEntityUrl(type, id));
  }
  emit();
}

/** Pop the top sheet — reveals the sheet beneath, or closes if empty. */
export function popEntity(): void {
  if (stack.length === 0) return;
  suppressNextPopstate++;
  stack = stack.slice(0, -1);
  if (typeof history !== 'undefined') {
    history.back();
  }
  emit();
}

/** Close the entire drill-through stack. */
export function closeEntities(): void {
  if (stack.length === 0) return;
  const depth = stack.length;
  stack = [];
  if (typeof history !== 'undefined') {
    suppressNextPopstate += depth;
    history.go(-depth);
  }
  emit();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function restoreFromUrl(): void {
  if (typeof window === 'undefined' || stack.length > 0) return;
  const match = window.location.hash.match(/^#v4\/entity\/([^\/]+)\/(.+)$/);
  if (match) {
    const type = decodeURIComponent(match[1]);
    const id = decodeURIComponent(match[2]);
    if (type && id) { stack = [{ type, id }]; emit(); }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (e: PopStateEvent) => {
    if (suppressNextPopstate > 0) { suppressNextPopstate--; return; }
    const newStack = (e.state as { entityStack?: EntityRef[] } | null)?.entityStack ?? [];
    stack = newStack;
    emit();
  });
}
